/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import React, { ReactElement } from "react";

import { useSelector } from "react-redux";
import { Link } from "react-router-dom";

import { Label, Stack, StackItem, Text } from "@fluentui/react";

import {
  bodyColumnStackItemStyles,
  bodyContainerStackStyles,
  bodyContainerStackTokens,
  bodyRowStackStyles,
  headerStackStyles,
  headerTextStyles,
  labelStyles,
  ContainerWrapper,
} from "Components/Common/SharedTableStyles.styles";
import { TenantTypesEnum } from "Constants/TenantTypes";
import { getUserProfileInfoBySelectedApplicationId } from "Dataflows/ApplicationDetails/ApplicationDetailsSelector";

const Nomination = (): ReactElement => {
  const profileInfo = useSelector(getUserProfileInfoBySelectedApplicationId);
  const nominatorForm = profileInfo?.nominatorForm;
  // As we know the current admin user has MVP access, prefer the nominator's MVP profile.
  const userProfileTenant = nominatorForm?.userProfileTenants.some(
    (tenant) => tenant.toLowerCase() == TenantTypesEnum.MVP.toLowerCase()
  )
    ? TenantTypesEnum.MVP.toLowerCase()
    : nominatorForm?.userProfileTenants[0]?.toLowerCase();

  return (
    <Stack>
      <ContainerWrapper>
        <Stack styles={headerStackStyles}>
          <Text styles={headerTextStyles}>Nominator Information</Text>
        </Stack>
        <Stack styles={bodyContainerStackStyles} tokens={bodyContainerStackTokens}>
          <Stack horizontal styles={bodyRowStackStyles}>
            <StackItem styles={bodyColumnStackItemStyles}>
              <Label styles={labelStyles}>First Name</Label>
              <Text>{nominatorForm?.firstName}</Text>
            </StackItem>
            <StackItem styles={bodyColumnStackItemStyles}>
              <Label styles={labelStyles}>Last Name</Label>
              <Text>{nominatorForm?.lastName}</Text>
            </StackItem>
          </Stack>
          {nominatorForm?.microsoftEmail && nominatorForm?.microsoftEmail && (
            <Stack horizontal styles={bodyRowStackStyles}>
              <StackItem styles={bodyColumnStackItemStyles}>
                <Label styles={labelStyles}>Email Address</Label>
                <Text>{nominatorForm?.microsoftEmail}</Text>
              </StackItem>
              <StackItem styles={bodyColumnStackItemStyles}>
                <Label styles={labelStyles}>Role at Microsoft</Label>
                <Text>{nominatorForm?.microsoftRole}</Text>
              </StackItem>
            </Stack>
          )}
          {nominatorForm?.userProfileId && (
            <Stack horizontal styles={bodyRowStackStyles}>
              <StackItem styles={bodyColumnStackItemStyles}>
                <Label styles={labelStyles}>Admin Center Profile Link</Label>
                <Link
                  to={`/${userProfileTenant?.toLowerCase()}/details/${
                    nominatorForm?.userProfileId
                  }`}
                >
                  {userProfileTenant?.toUpperCase()} User Profile {nominatorForm?.userProfileId}
                </Link>
              </StackItem>
            </Stack>
          )}
        </Stack>
      </ContainerWrapper>
      <ContainerWrapper>
        <Stack styles={headerStackStyles}>
          <Text styles={headerTextStyles}>Nominee Information</Text>
        </Stack>
        <Stack styles={bodyContainerStackStyles} tokens={bodyContainerStackTokens}>
          {nominatorForm?.nominatorReasons.map((reason) => (
            <Stack key={reason.id} horizontal styles={bodyRowStackStyles}>
              <StackItem>
                <Label styles={labelStyles}>{reason.questionText}</Label>
                <Text style={{ wordBreak: "break-word" }}>{reason.answerText}</Text>
              </StackItem>
            </Stack>
          ))}
        </Stack>
      </ContainerWrapper>
    </Stack>
  );
};

export default Nomination;
